import { useQuery } from "@tanstack/react-query";
import { DEMO_MODE } from "../lib/config";
import { api } from "../lib/api";
import { fmtCurrency } from "../lib/format";

interface ReportRow {
  sessionId: string;
  date: string;
  patientName: string;
  requested: number;
  approvedAmount: number | null;
  approvedLender: string | null;
  declines: number;
}

export function ReportsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["reports"],
    queryFn: async (): Promise<ReportRow[]> => {
      if (DEMO_MODE) return [];
      return await api.get<ReportRow[]>("/reports/sessions");
    },
  });

  const totalApproved = (data ?? []).reduce((sum, r) => sum + (r.approvedAmount ?? 0), 0);

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Reports</h1>
          <p className="page__sub">All financing sessions and outcomes.</p>
        </div>
        <div className="page__actions">
          <button
            className="btn btn--ghost"
            onClick={() => {
              if (DEMO_MODE) return;
              window.location.href = `${import.meta.env.VITE_API_BASE_URL}/reports/sessions.csv`;
            }}
            disabled={DEMO_MODE}
            title={!DEMO_MODE ? "Export CSV (audit-logged)" : "Available when the API is reachable"}
          >
            Export CSV
          </button>
        </div>
      </div>

      {isLoading && <div className="empty">Loading…</div>}
      {!isLoading && (!data || data.length === 0) && (
        <div className="empty">No sessions yet.</div>
      )}
      {!isLoading && data && data.length > 0 && (
        <>
          <p className="page__sub" style={{ marginBottom: 12 }}>
            Total approved this period: <strong>{fmtCurrency(totalApproved)}</strong>
          </p>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Patient</th>
                <th>Requested</th>
                <th>Approved</th>
                <th>Lender</th>
                <th>Declines</th>
              </tr>
            </thead>
            <tbody>
              {data.map((r) => (
                <tr key={r.sessionId}>
                  <td className="mono">{new Date(r.date).toLocaleDateString()}</td>
                  <td>{r.patientName}</td>
                  <td>{fmtCurrency(r.requested)}</td>
                  <td>{r.approvedAmount != null ? fmtCurrency(r.approvedAmount) : "—"}</td>
                  <td>{r.approvedLender ?? "—"}</td>
                  <td>{r.declines}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
